import React from 'react';

import { FilterRange, TableCell, TableHeaderCell } from '@hcs/design-system';
import { SpatialSortField } from '@hcs/types';
import {
  PropertyStateArgsProps,
  PropertyStateFieldConfig,
  PropertyStateFields,
  PropertyStateFilterProps,
  PropertyStatePaths,
  PropertyStateTableCellProps,
  PropertyStateTableHeaderCellProps,
  TableCellProps,
  TableHeaderCellProps,
} from '@hcs/types';
import { formatNumber, getPercentage, NULL_VALUE } from '@hcs/utils';

const FIELD = PropertyStateFields.rentalYield;
type FieldConfig = PropertyStateFieldConfig<typeof FIELD>;
const label = 'Rental Yield';
const labelShort = 'Rental Yield';
const propertyStatePath: PropertyStatePaths = '/propertyValueRental/yield';
const getValue: FieldConfig['getValue'] = (propertyStateArgs) => {
  const { propertyState, propertyStateType } = propertyStateArgs || {};
  if (propertyStateType === 'flat') {
    return propertyState?.[FIELD];
  } else if (propertyStateType === 'Core') {
    return propertyState?.propertyValueRental?.yield;
  }
  return undefined;
};
const formatValue: FieldConfig['formatValue'] = (propertyStateArgs) => {
  const value = getValue(propertyStateArgs);
  if (value != null) {
    return `${getPercentage(value, 1, false, false, 1)}%`;
  }
  return NULL_VALUE;
};

const formatInline: FieldConfig['formatInline'] = (propertyStateArgs) =>
  `${labelShort} ${formatValue(propertyStateArgs)}`;
// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({ propertyStateArgs }: PropertyStateArgsProps) => {
  return <>{formatValue(propertyStateArgs)}</>;
};
// eslint-disable-next-line react-refresh/only-export-components
const Filter = ({
  spatialFiltersInput,
  onChange,
  className,
  disabled,
  hideLabels,
}: PropertyStateFilterProps<typeof FIELD>) => {
  return (
    <FilterRange
      className={className}
      disabled={disabled}
      hideLabels={hideLabels}
      minVal={1}
      maxVal={99}
      showPercentSymbol
      initialValue={{
        min: spatialFiltersInput.minRentalYield
          ? formatNumber(spatialFiltersInput.minRentalYield * 100)
          : '',
        max: spatialFiltersInput.maxRentalYield
          ? formatNumber(spatialFiltersInput.maxRentalYield * 100)
          : '',
      }}
      onBlur={(value) => {
        onChange({
          field: FIELD,
          spatialFilterInputs: {
            minRentalYield: value.min === '' ? null : Number(value.min) / 100,
            maxRentalYield: value.max === '' ? null : Number(value.max) / 100,
          },
        });
      }}
      dataHcName={`${FIELD}-filter`}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: PropertyStateTableHeaderCellProps) => {
  return <TableHeaderCell {...props}>{labelShort}</TableHeaderCell>;
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'right',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  ...tableCellProps
}: PropertyStateTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      <Display propertyStateArgs={propertyStateArgs} />
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'right',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

export const RENTAL_YIELD_CONFIG: FieldConfig = {
  field: FIELD,
  label,
  labelShort,
  propertyStatePath,
  getValue,
  formatValue,
  formatInline,
  Filter,
  HeaderCell,
  ContentCell,
  Display,
  spatialSortField: SpatialSortField.RentalYield,
};
